import { useState, useCallback } from 'react';

import { createSMS, sendYelpMessage as sendYelpMessageApi } from '../../../../../../api';
import { uploadAttachments } from '../../../utils/uploadAttachments';
import { Attachment } from '../types';

const DEFAULT_SMS_ERROR_MESSAGE = 'Failed to send SMS';
const DEFAULT_YELP_ERROR_MESSAGE = 'Failed to send Yelp message';

type CreateSMSParams = {
  customerId: number;
  toPhoneNumber: string;
  message: string;
  fromPhoneNumber?: string;
  attachments: Attachment[];
};

type SendYelpMessageParams = {
  yelpLeadKey: string;
  message: string;
  attachments: Attachment[];
  toPhoneNumber?: string;
};

type UseCreateSMSResult = {
  sendSMS: (params: CreateSMSParams) => Promise<boolean>;
  sendYelpMessage: (params: SendYelpMessageParams) => Promise<boolean>;
  isSubmitting: boolean;
  error: string | null;
  setError: (error: string | null) => void;
};

export const useCreateSMS = (): UseCreateSMSResult => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendSMS = useCallback(async({ customerId, toPhoneNumber, message, fromPhoneNumber, attachments }: CreateSMSParams): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { urls } = await uploadAttachments(attachments);

      await createSMS(customerId, {
        to_phone_number: toPhoneNumber,
        message,
        ...(fromPhoneNumber && { from_phone_number: fromPhoneNumber }),
        ...(urls.length > 0 && { media_urls: urls }),
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : DEFAULT_SMS_ERROR_MESSAGE;
      setError(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const sendYelpMessage = useCallback(async({ yelpLeadKey, message, attachments, toPhoneNumber }: SendYelpMessageParams): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { urls } = await uploadAttachments(attachments);

      const filenames = attachments
        .filter((a) => a.file)
        .map((a) => a.file?.name || 'attachment');

      const attachmentPayload = urls.map((url, index) => ({
        url,
        filename: filenames[index] || `attachment-${index + 1}`,
      }));

      await sendYelpMessageApi({
        yelp_lead_key: yelpLeadKey,
        message,
        ...(toPhoneNumber && { to_phone_number: toPhoneNumber }),
        ...(attachmentPayload.length > 0 && { attachments: attachmentPayload }),
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : DEFAULT_YELP_ERROR_MESSAGE;
      setError(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { sendSMS, sendYelpMessage, isSubmitting, error, setError };
};
